export const getStringSimilarity = (s1, s2) => {
  const norm1 = (s1 || '').toLowerCase().trim();
  const norm2 = (s2 || '').toLowerCase().trim();
  if (!norm1 || !norm2) return 0;
  if (norm1 === norm2) return 1.0;
  
  const wordsA = norm1.split(/\s+/);
  const wordsB = norm2.split(/\s+/);
  const setB = new Set(wordsB);
  const intersection = wordsA.filter(x => setB.has(x));
  const overlapSim = intersection.length / Math.max(wordsA.length, wordsB.length);
  
  if (norm1.includes(norm2) || norm2.includes(norm1)) {
    return Math.max(overlapSim, 0.75);
  }
  return overlapSim;
};

export const getNearDuplicates = (families) => {
  const results = [];
  for (let i = 0; i < families.length; i++) {
    for (let j = i + 1; j < families.length; j++) {
      const f1 = families[i];
      const f2 = families[j];
      
      const g1 = f1.GameIDs || [];
      const g2 = f2.GameIDs || [];
      const overlapGames = g1.filter(id => g2.includes(id));
      const hasOverlap = overlapGames.length > 0;
      
      const nameSim = getStringSimilarity(f1.DisplayName, f2.DisplayName);
      const seriesSim = getStringSimilarity(f1.SeriesName, f2.SeriesName);
      const maxSim = Math.max(nameSim, seriesSim);
      const sameStudio = f1.StudioID === f2.StudioID;
      
      let reason = '';
      let score = 0;
      
      if (hasOverlap) {
        reason = `Shares ${overlapGames.length} game(s) in common`;
        score = 0.5 + 0.5 * (overlapGames.length / Math.min(g1.length, g2.length));
      } else if (maxSim >= 0.75 && sameStudio) {
        reason = `High name similarity (${Math.round(maxSim * 100)}%) in same studio`;
        score = maxSim;
      }
      
      if (score > 0) {
        results.push({
          familyA: f1,
          familyB: f2,
          reason,
          score,
          overlapGames
        });
      }
    }
  }
  return results.sort((a, b) => b.score - a.score);
};

export const getSuggestedNewFamilies = (allGames, families, studios, ignoredSeries = []) => {
  // 1. Filter out Live Dealer games
  const validGames = allGames.filter(g => !g.LiveDealer && g.Name && g.StudioID && g.GameID);
  
  // Stopwords definition
  const STOPWORDS = new Set([
    "game", "games", "slot", "slots", "the", "and", "for", "with", "from", "free", "new",
    "wilds", "wins", "win", "big"
  ]);
  
  // Generic single words
  const GENERIC_SINGLE_WORDS = new Set([
    "wild", "jackpot", "bonus", "bonuses", "coin", "coins", "fire", "gold", "diamond",
    "egypt", "aztec", "fortune", "magic", "super", "mega", "ultra", "deluxe", "classic",
    "power", "combo", "king", "queen", "double", "triple", "royal", "spin", "pots", "pot",
    "clover", "crown", "strike", "hot", "chili", "fruit", "link", "cash", "money", "rich",
    "riches", "ways", "multi", "grand", "major", "mini"
  ]);

  const MIN_WORD_LEN = 4;
  const MIN_GAMES_PER_PHRASE = 3;
  const MIN_SUPPORT_SINGLE = 5;

  // Normalizations
  const VARIANT_RULES = [
    [/\bhold\s*[\-&]?\s*n\s*win\b/gi, "hold and win"],
    [/\bhold\s*&\s*win\b/gi,          "hold and win"],
    [/\bhold\s*-\s*win\b/gi,          "hold and win"],
    [/\bpower\s*nudge\b/gi,           "powernudge"],
    [/\bmega\s*ways\b/gi,             "megaways"],
    [/\bmegaways™\b/gi,               "megaways"],
    [/&/g,                            " and "],
    [/[\-_/]+/g,                      " "]
  ];

  const normalizeText = (s) => {
    let text = (s || '').toLowerCase();
    for (const [pat, repl] of VARIANT_RULES) {
      text = text.replace(pat, repl);
    }
    text = text.replace(/[^a-z0-9\s]+/g, ' ');
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  const simpleSingularize = (token) => {
    if (token.length <= 4) return token;
    if (token.endsWith("ies") && token.length > 5) return token.slice(0, -3) + "y";
    if (token.endsWith("sses") || token.endsWith("shes") || token.endsWith("ches")) return token.slice(0, -2);
    if (token.endsWith("es") && !token.endsWith("ses")) return token.slice(0, -2);
    if (token.endsWith("s") && !token.endsWith("ss")) return token.slice(0, -1);
    return token;
  };

  const tokenizeWords = (s) => {
    const norm = normalizeText(s);
    return norm.split(' ').map(simpleSingularize).filter(t => t.length >= MIN_WORD_LEN);
  };

  const generateNgrams = (words, n) => {
    const ngrams = [];
    for (let i = 0; i <= words.length - n; i++) {
      ngrams.push(words.slice(i, i + n).join(' '));
    }
    return ngrams;
  };

  // Index of (StudioID, phrase) -> Set of GameIDs
  const studioPhraseGames = {};

  validGames.forEach(game => {
    const words = tokenizeWords(game.Name);
    if (words.length === 0) return;

    [1, 2, 3].forEach(n => {
      const ngrams = generateNgrams(words, n);
      ngrams.forEach(phrase => {
        // Check if all words in phrase are stopwords
        const phraseWords = phrase.split(' ');
        const isAllStopwords = phraseWords.every(w => STOPWORDS.has(w));
        if (isAllStopwords) return;

        const key = `${game.StudioID}||${phrase}`;
        if (!studioPhraseGames[key]) {
          studioPhraseGames[key] = new Set();
        }
        studioPhraseGames[key].add(game.GameID);
      });
    });
  });

  // Filter support threshold and generic single words
  const filteredPhrases = {};
  Object.entries(studioPhraseGames).forEach(([key, gameSet]) => {
    const [studioID, phrase] = key.split('||');
    const support = gameSet.size;
    if (support < MIN_GAMES_PER_PHRASE) return;

    const isSingle = phrase.split(' ').length === 1;
    if (isSingle && GENERIC_SINGLE_WORDS.has(phrase) && support < MIN_SUPPORT_SINGLE) return;
    if (isSingle && support < MIN_SUPPORT_SINGLE) return;

    filteredPhrases[key] = gameSet;
  });

  // Group by Studio
  const byStudio = {};
  Object.entries(filteredPhrases).forEach(([key, gameSet]) => {
    const [studioID, phrase] = key.split('||');
    if (!byStudio[studioID]) {
      byStudio[studioID] = [];
    }
    byStudio[studioID].push({ phrase, gameSet });
  });

  const results = [];

  // For each studio, merge overlapping candidate phrases using Jaccard and containment logic
  Object.entries(byStudio).forEach(([studioID, items]) => {
    let working = items.map(item => ({ phrase: item.phrase, gameSet: item.gameSet }));
    let changed = true;

    const jaccard = (setA, setB) => {
      const intersection = new Set([...setA].filter(x => setB.has(x)));
      const union = new Set([...setA, ...setB]);
      return intersection.size / union.size;
    };

    const containmentRatio = (setA, setB) => {
      const intersection = new Set([...setA].filter(x => setB.has(x)));
      return intersection.size / Math.min(setA.size, setB.size);
    };

    const bestLabel = (labelA, labelB, preferBroader) => {
      const lenA = labelA.split(' ').length;
      const lenB = labelB.split(' ').length;
      if (preferBroader) {
        if (lenA !== lenB) {
          return lenA < lenB ? labelA : labelB;
        }
        return labelA.length >= labelB.length ? labelA : labelB;
      }
      return labelA.length >= labelB.length ? labelA : labelB;
    };

    while (changed && working.length > 1) {
      changed = false;
      const newWorking = [];
      const used = new Array(working.length).fill(false);

      for (let i = 0; i < working.length; i++) {
        if (used[i]) continue;
        let mergedLabel = working[i].phrase;
        let mergedSet = new Set(working[i].gameSet);

        for (let j = i + 1; j < working.length; j++) {
          if (used[j]) continue;
          const setJ = working[j].gameSet;
          const jac = jaccard(mergedSet, setJ);
          const cont = containmentRatio(mergedSet, setJ);

          const isLinguisticallyRelated = () => {
            const labelA = mergedLabel;
            const labelB = working[j].phrase;
            if (labelA === labelB) return true;
            const wordsA = labelA.split(' ');
            const wordsB = labelB.split(' ');
            
            if (labelA.includes(labelB) || labelB.includes(labelA)) {
              const isGenericA = wordsA.length === 1 && GENERIC_SINGLE_WORDS.has(wordsA[0]);
              const isGenericB = wordsB.length === 1 && GENERIC_SINGLE_WORDS.has(wordsB[0]);
              if (isGenericA || isGenericB) {
                return false;
              }
              return true;
            }
            
            const setA = new Set(wordsA);
            const setB = new Set(wordsB);
            const intersection = new Set([...setA].filter(x => setB.has(x)));
            const union = new Set([...setA, ...setB]);
            return (intersection.size / union.size) >= 0.5;
          };

          const related = isLinguisticallyRelated();
          if (jac >= 0.72 || (cont >= 0.85 && related)) {
            setJ.forEach(x => mergedSet.add(x));
            const preferBroad = (cont >= 0.85);
            mergedLabel = bestLabel(mergedLabel, working[j].phrase, preferBroad);
            used[j] = true;
            changed = true;
          }
        }
        used[i] = true;
        newWorking.push({ phrase: mergedLabel, gameSet: mergedSet });
      }
      working = newWorking;
    }

    // Drop exact duplicates (identical GameID sets)
    const uniqueClusters = [];
    working.forEach(item => {
      const sortedIDs = Array.from(item.gameSet).sort().join(',');
      const existingIdx = uniqueClusters.findIndex(u => u.idString === sortedIDs);
      if (existingIdx >= 0) {
        uniqueClusters[existingIdx].phrase = bestLabel(uniqueClusters[existingIdx].phrase, item.phrase, true);
      } else {
        uniqueClusters.push({ phrase: item.phrase, gameSet: item.gameSet, idString: sortedIDs });
      }
    });

    const studioObj = studios.find(s => s.StudioID === studioID);
    const studioName = studioObj ? studioObj.Name : "Unknown Studio";

    uniqueClusters.forEach(cluster => {
      const gameIDs = Array.from(cluster.gameSet);
      const seriesName = cluster.phrase;
      
      // Filter out if this is already an existing family
      const slug = seriesName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const jsonKey = `${studioName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}_${slug}_series`;
      
      const alreadyExists = families.some(f => 
        f.FamilyID === jsonKey || 
        f.Slug === slug ||
        f.SeriesName.toLowerCase() === seriesName.toLowerCase()
      );

      const isIgnored = ignoredSeries.includes(seriesName.toLowerCase());

      const allGamesCataloged = gameIDs.every(id => 
        families.some(f => (f.GameIDs || []).includes(id))
      );

      if (!alreadyExists && !isIgnored && !allGamesCataloged) {
        results.push({
          StudioID: studioID,
          StudioName: studioName,
          SeriesName: seriesName,
          GameIDs: gameIDs,
          GameCount: gameIDs.length
        });
      }
    });
  });

  return results.sort((a, b) => b.GameCount - a.GameCount);
};

export const getSuggestedAddFamilies = (families, allGames, studios) => {
  const results = [];
  
  families.forEach(family => {
    const studioID = family.StudioID;
    const seriesName = (family.SeriesName || '').toLowerCase().trim();
    if (!seriesName) return;

    const knownIDs = new Set(family.GameIDs || []);

    // Filter games from this studio whose name contains the series name as a substring
    const matches = allGames.filter(game => 
      game.StudioID === studioID && 
      !game.LiveDealer &&
      game.Name && 
      game.Name.toLowerCase().includes(seriesName) &&
      !knownIDs.has(game.GameID)
    );

    const studioObj = studios.find(s => s.StudioID === studioID);
    const studioName = studioObj ? studioObj.Name : "Unknown Studio";

    if (matches.length > 0) {
      results.push({
        family,
        studioName,
        newGames: matches
      });
    }
  });

  return results;
};
